import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { Send, Loader2, ArrowLeft, Filter } from "lucide-react";
import apiClient from "../../services/apiClient";
import { useNavigate } from "react-router-dom";

type NotificationPayload = {
  group?: string;
  message: string;
  type: string;
  subject?: string;
  postcode?: string;
  petType?: string;
  gender?: string;
  species?: string;
  breed_group?: string;
  age?: number;
  dob?: string;
};

const genders = ["male", "female", "other"];

const SendNotificationForm: React.FC = () => {
  const navigate = useNavigate();

  const [petTypes, setPetTypes] = useState<string[]>([]);
  const [speciesOptions, setSpeciesOptions] = useState<string[]>([]);
  const [breedOptions, setBreedOptions] = useState<string[]>([]);

  const [formData, setFormData] = useState<NotificationPayload>({
    group: "",
    message: "",
    type: "push",
    subject: "",
    postcode: "",
    petType: "",
    gender: "",
    species: "",
    breed_group: "",
    age: undefined,
    dob: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  // ✅ Fetch pet types
  useEffect(() => {
    const fetchPetTypes = async () => {
      try {
        const res: any = await apiClient("/v1/resource/masters/all?slug=pet-type");
        if (res?.data?.masters?.[0]?.attributes?.values) {
          setPetTypes(res.data.masters[0].attributes.values);
        }
      } catch (err) {
        console.error("Failed to fetch pet types:", err);
      }
    };
    fetchPetTypes();
  }, []);

  // ✅ Fetch species & breeds based on selected pet type
  useEffect(() => {
    const fetchSpeciesAndBreeds = async () => {
      const petType = formData.petType?.toLowerCase() || "";
      if (!petType) {
        setSpeciesOptions([]);
        setBreedOptions([]);
        setFormData((prev) => ({ ...prev, species: "", breed_group: "" }));
        return;
      }
      try {
        const res: any = await apiClient(
          `/v1/resource/masters/all?slug=${petType}&order_by=name&dir=1`
        );
        const masters = res?.data?.masters || [];
        const speciesSet = new Set<string>();
        const breedSet = new Set<string>();
        masters.forEach((m: any) => {
          if (m.name) speciesSet.add(m.name);
          if (m.attributes?.breed_group) breedSet.add(m.attributes.breed_group);
        });
        setSpeciesOptions(Array.from(speciesSet));
        setBreedOptions(Array.from(breedSet));
        setFormData((prev) => ({
          ...prev,
          species: speciesSet.has(prev.species || "") ? prev.species : "",
          breed_group: breedSet.has(prev.breed_group || "") ? prev.breed_group : "",
        }));
      } catch (err) {
        console.error("Failed to fetch species/breeds:", err);
      }
    };

    fetchSpeciesAndBreeds();
  }, [formData.petType]);

  // ✅ Clear response messages after 5 seconds
  useEffect(() => {
    if (responseMessage) {
      const timer = setTimeout(() => setResponseMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [responseMessage]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "age" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage(null);

    // 🧠 Optional: Basic Postcode validation
    if (formData.postcode && !/^\d{6}$/.test(formData.postcode)) {
      setResponseMessage("❌ Please enter a valid 6-digit postcode");
      setIsError(true);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        dob: formData.dob ? new Date(formData.dob).toISOString().split("T")[0] : "",
      };

      await apiClient("/v1/notifications/send", {
        method: "POST",
        body: payload,
      });

      setResponseMessage("✅ Notification sent successfully!");
      setIsError(false);
      setFormData({
        group: "",
        message: "",
        type: "push",
        subject: "",
        postcode: "",
        petType: "",
        gender: "",
        species: "",
        breed_group: "",
        age: undefined,
        dob: "",
      });
    } catch (error: any) {
      setResponseMessage(`❌ ${error.message || "Something went wrong"}`);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 animate-fade-in">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden ring-1 ring-indigo-500/10 dark:ring-indigo-400/10 transition-all duration-500 hover:shadow-indigo-500/20 dark:hover:shadow-indigo-400/20">
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 flex items-center justify-between relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.2),transparent)] opacity-50"></div>
            <h2 className="text-2xl font-bold text-white relative z-10 drop-shadow-lg">Send Notification</h2>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white hover:bg-white/10 px-4 py-2 rounded-xl transition-all duration-300 backdrop-blur-sm relative z-10"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-8">
            {/* Target Group + Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Target Group */}
              <div className="relative group">
                <label
                  htmlFor="group"
                  className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                >
                  Target Group <span className="text-pink-500">*</span>
                </label>
                <select
                  id="group"
                  name="group"
                  value={formData.group}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                >
                  <option value="">Select a group</option>
                  <option value="parent">Parent</option>
                  <option value="vendors">Vendors</option>
                  <option value="service_providers">Service Providers</option>
                  <option value="veterinarians">Veterinarians</option>
                  <option value="organization">Organization</option>
                  <option value="facility">Facility</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Notification Type */}
              <div className="relative group">
                <label
                  htmlFor="type"
                  className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                >
                  Notification Type <span className="text-pink-500">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                >
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                  <option value="push">Push</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>

            {/* 📍 Filters Section */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwSDEwMFYyMDBIMFYweiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZWZlZmZmIiBvcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEwIi8+PC9zdmc+')] opacity-50 dark:opacity-10"></div>
              <h3 className="text-lg font-bold text-indigo-700 dark:text-indigo-300 mb-6 flex items-center gap-3 relative z-10">
                <Filter size={20} className="text-pink-500" />
                Target Filters
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                {/* Postcode */}
                <div className="relative group">
                  <label
                    htmlFor="postcode"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Postcode
                  </label>
                  <input
                    type="text"
                    id="postcode"
                    name="postcode"
                    value={formData.postcode || ""}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="e.g. 600001"
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  />
                </div>

                {/* Pet Type */}
                <div className="relative group">
                  <label
                    htmlFor="petType"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Pet Type
                  </label>
                  <select
                    name="petType"
                    value={formData.petType}
                    onChange={handleChange}
                    disabled={loading || petTypes.length === 0}
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  >
                    <option value="">Any</option>
                    {petTypes.map((pt) => (
                      <option key={pt} value={pt}>
                        {pt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gender */}
                <div className="relative group">
                  <label
                    htmlFor="gender"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  >
                    <option value="">Any</option>
                    {genders.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Age */}
                <div className="relative group">
                  <label
                    htmlFor="age"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Age
                  </label>
                  <input
                    type="number"
                    name="age"
                    min={0}
                    value={formData.age ?? ""}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Optional"
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  />
                </div>

                {/* DOB */}
                <div className="relative group">
                  <label
                    htmlFor="dob"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    DOB
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  />
                </div>

                {/* Species */}
                <div className="relative group">
                  <label
                    htmlFor="species"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Species
                  </label>
                  <select
                    name="species"
                    value={formData.species}
                    onChange={handleChange}
                    disabled={loading || speciesOptions.length === 0}
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  >
                    <option value="">Any</option>
                    {speciesOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Breed Group */}
                <div className="relative group col-span-1 sm:col-span-2 lg:col-span-1">
                  <label
                    htmlFor="breed_group"
                    className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
                  >
                    Breed Group
                  </label>
                  <select
                    name="breed_group"
                    value={formData.breed_group}
                    onChange={handleChange}
                    disabled={loading || breedOptions.length === 0}
                    className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner"
                  >
                    <option value="">Any</option>
                    {breedOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="relative group">
              <label
                htmlFor="message"
                className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 transition-all duration-300 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400"
              >
                Message <span className="text-pink-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full rounded-xl border border-gray-300/50 dark:border-gray-700/50 p-3 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 shadow-inner resize-y"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 rounded-2xl py-4 px-6 font-bold text-white bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {loading ? (
                <>
                  <Loader2 size={24} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send size={24} /> Send Notification
                </>
              )}
            </button>

            {/* Response */}
            {responseMessage && (
              <div
                className={`mt-4 text-center text-base font-medium p-4 rounded-2xl transition-all duration-500 transform ${
                  isError
                    ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 scale-105"
                    : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 scale-105"
                }`}
                role="alert"
              >
                {responseMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default SendNotificationForm;